import { type NavigateOptions, Link as TanstackLink } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';
import type { ReactNode } from 'react';

interface StyledLinkProps {
  navigateTo: NavigateOptions;
  text: ReactNode;
}

export function StyledLink(props: StyledLinkProps) {
  const { navigateTo, text } = props;

  return (
    <TanstackLink
      {...navigateTo}
      className="inline-flex items-center gap-1 text-primary hover:underline"
      // Table rows navigate on click too: the link alone must win.
      onClick={(event) => event.stopPropagation()}
    >
      {text}
      <ArrowUpRight className="size-4 shrink-0" />
    </TanstackLink>
  );
}

type LinkedEntity = 'supplier' | 'employee' | 'prestation' | 'order' | 'invoice';

function entityNavigateOptions(entity: LinkedEntity, id: number): NavigateOptions {
  const params = { id: String(id) };
  switch (entity) {
    case 'supplier':
      return { to: '/$commiteeSlug/suppliers/{-$id}', params };
    case 'employee':
      return { to: '/$commiteeSlug/beneficiaries/employees/{-$id}', params };
    case 'prestation':
      return { to: '/$commiteeSlug/prestation/{-$id}', params };
    case 'order':
      return { to: '/$commiteeSlug/orders/$orderId', params: { orderId: String(id) } };
    case 'invoice':
      return { to: '/$commiteeSlug/invoices/$invoiceId', params: { invoiceId: String(id) } };
  }
}

interface EntityLinkProps {
  entity: LinkedEntity;
  /** Without an id (an unsaved or unknown entity) the name renders as plain text. */
  id: number | null | undefined;
  children: ReactNode;
}

export function EntityLink({ entity, id, children }: EntityLinkProps) {
  if (id == null) return <span>{children}</span>;
  return <StyledLink navigateTo={entityNavigateOptions(entity, id)} text={children} />;
}
